import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PlatformService } from '../../services/platform.service';
import { PlatformMarketplaceDto } from '../../models/platform-marketplace.dto';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-marketplace',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ProgressSpinnerModule
  ],
  templateUrl: './marketplace.component.html',
  styleUrl: './marketplace.component.css'
})
export class MarketplaceComponent implements OnInit {
  platforms = signal<PlatformMarketplaceDto[]>([]);
  promoStores = signal<PlatformMarketplaceDto[]>([]);
  showPromoOnly = signal(false);
  loading = signal(true);
  error = signal<string | null>(null);
  searchQuery = signal('');
  selectedCategory = signal<string | null>(null);

  categories = [
    { label: 'All Stores', value: null, icon: 'pi-th-large' },
    { label: 'Electronics & Technology', value: 'electronics-technology', icon: 'pi-mobile' },
    { label: 'Fashion & Apparel', value: 'fashion-apparel', icon: 'pi-shopping-bag' },
    { label: 'Home & Garden', value: 'home-garden', icon: 'pi-home' },
    { label: 'Health & Beauty', value: 'health-beauty', icon: 'pi-star' },
    { label: 'Sports & Recreation', value: 'sports-recreation', icon: 'pi-circle' },
    { label: 'Books & Media', value: 'books-media', icon: 'pi-book' },
    { label: 'Automotive', value: 'automotive', icon: 'pi-car' },
    { label: 'Food & Beverages', value: 'food-beverages', icon: 'pi-shopping-cart' },
    { label: 'Toys & Games', value: 'toys-games', icon: 'pi-thumbs-up' },
    { label: 'Pet Supplies', value: 'pet-supplies', icon: 'pi-heart' },
    { label: 'Office & Business', value: 'office-business', icon: 'pi-briefcase' },
    { label: 'Industrial & Scientific', value: 'industrial-scientific', icon: 'pi-cog' },
    { label: 'Solar Energy', value: 'solar-energy', icon: 'pi-sun' },
    { label: 'Inverters', value: 'inverters', icon: 'pi-bolt' }
  ];

  filteredPlatforms = computed(() => {
    let filtered = this.platforms();
    const query = this.searchQuery().toLowerCase().trim();
    const onlyPromo = this.showPromoOnly();
    const promoIds = new Set(this.promoStores().map(p => p.id));

    if (onlyPromo && promoIds.size > 0) {
      filtered = filtered.filter(p => promoIds.has(p.id));
    }
    if (query) {
      filtered = filtered.filter(p =>
        p.platformName.toLowerCase().includes(query) ||
        (p.businessName && p.businessName.toLowerCase().includes(query)) ||
        p.country.toLowerCase().includes(query)
      );
    }
    return filtered;
  });

  constructor(
    private platformService: PlatformService,
    private settingsService: SettingsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadPlatforms();
    this.loadPromoStores();
  }

  loadPromoStores(): void {
    const env = this.settingsService.getEnvironment();
    this.platformService.getPromoStores(env).subscribe({
      next: (res) => {
        if (res.success && res.data?.length) {
          this.promoStores.set(res.data);
        }
      },
      error: () => this.promoStores.set([])
    });
  }

  viewDiscountStores(): void {
    this.showPromoOnly.set(true);
    this.searchQuery.set('');
    this.selectedCategory.set(null);
    this.loadPlatforms();
    document.querySelector('.platforms-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  clearPromoFilter(): void {
    this.showPromoOnly.set(false);
    this.loadPlatforms();
  }

  loadPlatforms(): void {
    this.loading.set(true);
    this.error.set(null);

    const categoryName = this.selectedCategory();
    this.platformService.getPlatforms(categoryName).subscribe({
      next: (response) => {
        if (response.success) {
          this.platforms.set(response.data);
        } else {
          this.error.set(response.message || 'Failed to load platforms');
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load platforms. Please try again later.');
        this.loading.set(false);
        console.error('Error loading platforms:', err);
      }
    });
  }


  onCategoryChange(): void {
    // Reload platforms when category changes
    this.loadPlatforms();
  }

  getLogoUrl(logo: string | null): string {
    if (!logo) {
      return 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=300&fit=crop';
    }
    return logo;
  }

  onPlatformClick(platform: PlatformMarketplaceDto): void {
    // Navigate to store page with platform ID and name
    const encodedName = encodeURIComponent(platform.platformName);
    this.router.navigate(['/store', platform.id, encodedName]);
  }

  getSelectedCategoryLabel(): string {
    const selected = this.selectedCategory();
    if (!selected) {
      return 'All Stores';
    }
    const category = this.categories.find(c => c.value === selected);
    return category?.label || 'Stores';
  }

}